/**
 * Shared splash/loading overlay for map views.
 * Used by both the main App and the embed (EmbedApp).
 *
 * variant="full"  — full-viewport overlay with logo images + dark mode support (App.tsx)
 * variant="embed" — inline overlay with inline SVG logo, dark only (EmbedApp)
 */

interface MapSplashLoaderProps {
    variant?: 'full' | 'embed';
}

export default function MapSplashLoader({ variant = 'full' }: MapSplashLoaderProps) {
    if (variant === 'embed') {
        return (
            <div className="absolute inset-0 bg-gray-900 flex items-center justify-center z-20">
                <div className="text-center">
                    <div className="relative w-14 h-14 mx-auto mb-3">
                        <svg className="w-14 h-14" viewBox="0 0 512 512" fill="none">
                            <ellipse cx="256" cy="256" rx="102" ry="218" stroke="#10b981" strokeWidth="19" fill="none" opacity="0.7" />
                            <ellipse cx="256" cy="256" rx="218" ry="102" stroke="#10b981" strokeWidth="19" fill="none" opacity="0.7" />
                            <line x1="38" y1="256" x2="474" y2="256" stroke="#10b981" strokeWidth="26" opacity="0.35" />
                            <line x1="256" y1="38" x2="256" y2="474" stroke="#10b981" strokeWidth="26" opacity="0.35" />
                            <circle cx="333" cy="179" r="64" fill="#10b981" />
                            <circle cx="333" cy="179" r="26" fill="white" />
                        </svg>
                        <div className="absolute inset-0 rounded-full border-2 border-emerald-500/30 animate-ping" />
                    </div>
                    <p className="text-white/90 font-semibold text-sm tracking-wide">Coordal Maps</p>
                    <p className="mt-1 text-white/40 text-xs">Loading map…</p>
                </div>
            </div>
        );
    }

    // variant === 'full'
    return (
        <div className="fixed inset-0 bg-white dark:bg-gray-900 flex items-center justify-center z-[200]">
            <div className="text-center">
                <div className="relative w-20 h-20 mx-auto mb-4">
                    <svg className="w-20 h-20 animate-pulse" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Coordal">
                        <defs>
                            <linearGradient id="splashGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#06b6d4" stopOpacity="1" />
                                <stop offset="100%" stopColor="#3b82f6" stopOpacity="1" />
                            </linearGradient>
                        </defs>
                        <ellipse cx="20" cy="20" rx="8" ry="17" stroke="url(#splashGrad)" strokeWidth="1.5" fill="none" opacity="0.7" />
                        <ellipse cx="20" cy="20" rx="17" ry="8" stroke="url(#splashGrad)" strokeWidth="1.5" fill="none" opacity="0.7" />
                        <line x1="3" y1="20" x2="37" y2="20" stroke="#06b6d4" strokeWidth="2" opacity="0.4" />
                        <line x1="20" y1="3" x2="20" y2="37" stroke="#06b6d4" strokeWidth="2" opacity="0.4" />
                        <circle cx="26" cy="14" r="5" fill="url(#splashGrad)" />
                        <circle cx="26" cy="14" r="2" fill="white" />
                    </svg>
                    <div className="absolute inset-0 rounded-full border-2 border-cyan-500/30 animate-ping" />
                </div>
                <p className="text-gray-900 dark:text-white/90 font-semibold text-lg tracking-wide">Coordal</p>
                <p className="mt-1 text-gray-400 dark:text-white/40 text-xs">Loading map…</p>
            </div>
        </div>
    );
}
